import { IProjectMemberApiItem } from "@schemas/projectSchema";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  Tooltip,
  Transition,
  UnstyledButton,
} from "@mantine/core";
import { IconArrowUp, IconSparkles } from "@tabler/icons-react";
import type { PostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiApiResponse } from "../../../../../../openapi";
import { useEffect, useRef, useState } from "react";

export const ISSUE_ASK_AI_PANEL_WIDTH = 420;
export const ISSUE_ASK_AI_PANEL_GAP = 8;

type IssueAskAiExplanation = NonNullable<
  PostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiApiResponse["explanation"]
>;

type IssueAskAiMode = IssueAskAiExplanation["mode"];

type IssueAskAiResultVisibilitySummary = {
  totalAiResults: number;
  visibleAiResults: number;
  hiddenByFrontendFilters: number;
};

export type IssueAskAiPanelPerson = {
  field: string;
  projectMember: IProjectMemberApiItem;
};

type IssueAskAiPanelResult = {
  question: string;
  explanation: IssueAskAiExplanation;
  people: IssueAskAiPanelPerson[];
};

type IssueAskAiPanelProps = {
  opened: boolean;
  isLoading: boolean;
  result: IssueAskAiPanelResult | null;
  resultVisibilitySummary: IssueAskAiResultVisibilitySummary | null;
  submitDisabled: boolean;
  onClose: () => void;
  onSubmit: (question: string) => Promise<void> | void;
  onPersonClick: (memberId: string) => void;
};

const issueAskAiModeLabels: Record<IssueAskAiMode, string> = {
  FILTER: "Databasefilter",
  SEMANTIC_SEARCH: "Semantisk søk",
};

const IssueAskAiModeBadge: React.FC<{ mode: IssueAskAiMode }> = ({ mode }) => (
  <Badge color={mode === "SEMANTIC_SEARCH" ? "violet" : "blue"} variant="light">
    {issueAskAiModeLabels[mode]}
  </Badge>
);

const buildIssueAskAiGeneratedText = (args: {
  mode: IssueAskAiMode;
  summary?: string;
  filters?: string[];
  resultVisibilityText: string | null;
}): string => {
  const resultText = args.resultVisibilityText ?? "";
  if (args.summary != null && args.summary.trim().length > 0) {
    return [args.summary, resultText].filter(Boolean).join(" ");
  }

  if (args.mode === "SEMANTIC_SEARCH") {
    return ["Søker etter saker som virker semantisk relevante for spørsmålet ditt.", resultText]
      .filter(Boolean)
      .join(" ");
  }

  const filterText =
    args.filters != null && args.filters.length > 0
      ? `Filtrerer fram saker der ${args.filters.join(", ")}.`
      : "Filtrerer fram saker basert på spørsmålet ditt.";
  return [filterText, resultText].filter(Boolean).join(" ");
};

const IssueAskAiGeneratedText: React.FC<{
  text: string;
  people: IssueAskAiPanelPerson[];
  onPersonClick: (memberId: string) => void;
}> = ({ text, people, onPersonClick }) => {
  const [visibleText, setVisibleText] = useState(text);

  useEffect(() => {
    if (text.length === 0) {
      setVisibleText("");
      return;
    }

    setVisibleText("");
    let index = 0;
    const interval = window.setInterval(() => {
      index += 3;
      setVisibleText(text.slice(0, index));
      if (index >= text.length) {
        window.clearInterval(interval);
      }
    }, 16);

    return () => window.clearInterval(interval);
  }, [text]);

  const linkedPerson = people.find(({ projectMember }) => text.includes(projectMember.user.name));
  const personNameStart = linkedPerson == null ? -1 : text.indexOf(linkedPerson.projectMember.user.name);
  const showLinkedName = personNameStart >= 0 && visibleText === text;

  return (
    <Text size="sm" c="gray.3" style={{ minHeight: 44 }}>
      {showLinkedName && linkedPerson != null ? (
        <>
          {text.slice(0, personNameStart)}
          <Tooltip label={`Vis ${linkedPerson.projectMember.user.name}`}>
            <UnstyledButton
              component="span"
              onClick={() => onPersonClick(linkedPerson.projectMember._id)}
              style={{ color: "var(--mantine-color-violet-2)", textDecoration: "underline", cursor: "pointer" }}
            >
              {linkedPerson.projectMember.user.name}
            </UnstyledButton>
          </Tooltip>
          {text.slice(personNameStart + linkedPerson.projectMember.user.name.length)}
        </>
      ) : (
        visibleText
      )}
    </Text>
  );
};

const issueAskAiExamplePrompts = [
  { label: "Mine frister", prompt: "vis mine saker som har passert tidsfrist" },
  { label: "Semantisk søk", prompt: "vis saker som er relaterte til brann" },
  { label: "Nyeste", prompt: "vis saker som ble opprettet siste måned, nyeste først" },
  { label: "Ansvarlig", prompt: "vis saker der jeg er ansvarlig for utførelse" },
];

export const IssueAskAiPanel: React.FC<IssueAskAiPanelProps> = ({
  opened,
  isLoading,
  result,
  resultVisibilitySummary,
  submitDisabled,
  onClose,
  onSubmit,
  onPersonClick,
}) => {
  const [question, setQuestion] = useState("");
  const [submittedQuestion, setSubmittedQuestion] = useState<string | null>(result?.question ?? null);
  const [hasStartedConversation, setHasStartedConversation] = useState(result != null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (opened) {
      setQuestion("");
      setSubmittedQuestion(result?.question ?? null);
      setHasStartedConversation(result != null);
      window.setTimeout(() => textareaRef.current?.focus(), 180);
    }
  }, [opened]);

  const submit = async () => {
    const trimmedQuestion = question.trim();
    if (trimmedQuestion.length === 0 || submitDisabled) {
      return;
    }

    setHasStartedConversation(true);
    setSubmittedQuestion(trimmedQuestion);
    setQuestion("");
    await onSubmit(trimmedQuestion);
  };

  const activeMode = result?.explanation.mode;
  const resultTypeLabel = activeMode === "SEMANTIC_SEARCH" ? "relaterte saker" : "saker";
  const resultVisibilityText =
    result == null || resultVisibilitySummary == null
      ? null
      : resultVisibilitySummary.totalAiResults === 0
      ? activeMode === "SEMANTIC_SEARCH"
        ? "Jeg fant ingen tydelige treff denne gangen. 😕"
        : "Jeg fant ingen saker som matcher dette akkurat nå. 😕"
      : resultVisibilitySummary.hiddenByFrontendFilters > 0
      ? `AI fant totalt ${resultVisibilitySummary.totalAiResults} ${resultTypeLabel}. Av disse vises ${resultVisibilitySummary.visibleAiResults} basert på dine aktive filtre i dette registeret.`
      : `AI fant ${resultVisibilitySummary.totalAiResults} ${resultTypeLabel}.`;
  const generatedText =
    result != null && activeMode != null
      ? buildIssueAskAiGeneratedText({
          mode: activeMode,
          summary: result.explanation.summary,
          filters: result.explanation.filters,
          resultVisibilityText,
        })
      : "";
  const displayedQuestion = isLoading ? submittedQuestion : result?.question ?? submittedQuestion;
  const showExamplePrompts = result == null && question.trim().length === 0;

  const prompt = (
    <Stack gap="sm">
      <Paper withBorder radius="sm" px="sm" py={6} bg="dark.6" shadow="md" bd="1px solid var(--mantine-color-dark-4)">
        <Group gap="xs" align="end" wrap="nowrap">
          <Textarea
            ref={textareaRef}
            autoFocus
            autosize
            minRows={1}
            maxRows={4}
            placeholder="Spør om saker i registeret"
            value={question}
            onChange={(event) => setQuestion(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && event.shiftKey === false) {
                event.preventDefault();
                void submit();
              }
              if (event.key === "Escape") {
                onClose();
              }
            }}
            variant="unstyled"
            size="sm"
            c="gray.0"
            styles={{
              input: { border: 0, boxShadow: "none", background: "transparent", color: "var(--mantine-color-gray-0)" },
            }}
            style={{ flex: 1 }}
          />
          <Tooltip label="Send">
            <ActionIcon
              size="md"
              radius="md"
              color="violet"
              variant="light"
              loading={isLoading}
              disabled={question.trim().length === 0 || submitDisabled}
              onClick={() => void submit()}
            >
              <IconArrowUp size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Paper>
      {showExamplePrompts && (
        <Stack gap={6}>
          <Text size="xs" c="gray.5">
            Prøv for eksempel
          </Text>
          <Group gap={6} wrap="wrap">
            {issueAskAiExamplePrompts.map((example) => (
              <Button
                key={example.label}
                size="xs"
                variant="light"
                color="gray"
                radius="sm"
                onClick={() => {
                  setQuestion(example.prompt);
                  textareaRef.current?.focus();
                }}
              >
                {example.label}
              </Button>
            ))}
          </Group>
        </Stack>
      )}
    </Stack>
  );

  return (
    <Transition mounted={opened} transition="slide-left" duration={180} timingFunction="ease">
      {(transitionStyles) => (
        <Paper
          withBorder
          radius="md"
          p="sm"
          bg="dark.7"
          shadow="lg"
          style={{
            ...transitionStyles,
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: ISSUE_ASK_AI_PANEL_WIDTH,
            height: "100%",
            overflow: "hidden",
            borderColor: "var(--mantine-color-dark-4)",
          }}
        >
          <Stack gap="sm" style={{ width: 388, height: "100%" }}>
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <IconSparkles size={18} color="var(--mantine-color-violet-3)" />
                <Text fw={600} c="gray.1">
                  Spør AI (beta)
                </Text>
              </Group>
              <CloseButton size="sm" c="gray.3" onClick={onClose} title="Lukk AI-panel" />
            </Group>
            <Box style={{ position: "relative", flex: 1, minHeight: 0 }}>
              <Stack
                gap="xs"
                style={{ maxHeight: "100%", overflowY: "auto", paddingBottom: hasStartedConversation ? "5rem" : 0 }}
              >
                {displayedQuestion != null && (
                  <Paper bg="violet.9" px="sm" py={6} radius="sm" ml="auto" maw="90%">
                    <Text size="sm" c="white">
                      {displayedQuestion}
                    </Text>
                  </Paper>
                )}
                {isLoading && (
                  <Text size="sm" c="gray.4" fs="italic">
                    Tolker spørsmålet og søker i registeret...
                  </Text>
                )}
                {!isLoading && result != null && activeMode != null && (
                  <>
                    <Divider />
                    <Stack gap="xs">
                      <Group justify="space-between" gap="xs" wrap="nowrap">
                        <Text size="sm" fw={600} c="gray.1">
                          Tolkning
                        </Text>
                        <IssueAskAiModeBadge mode={activeMode} />
                      </Group>
                      <IssueAskAiGeneratedText
                        text={generatedText}
                        people={result.people}
                        onPersonClick={onPersonClick}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
              <Box
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: hasStartedConversation ? 0 : "50%",
                  left: 0,
                  transform: hasStartedConversation ? "translateY(0)" : "translateY(50%)",
                  transition: "bottom 260ms ease, transform 260ms ease",
                }}
              >
                {prompt}
              </Box>
            </Box>
          </Stack>
        </Paper>
      )}
    </Transition>
  );
};
